/*
 * Shilajit Global — shoppable hero hotspot layer.
 *
 * WHAT THIS FILE DOES NOT DO
 * It does not run the size switcher. That is a native radio group driven by a
 * single CSS adjacent-sibling rule, so it works with this script blocked and
 * needs no state here. It does not read scroll position, run a rAF loop, or
 * animate anything — assets/sg-scroll-hero.js owns all of that and emits one
 * event this file listens to.
 *
 * So there are exactly three jobs, all of them things CSS cannot express:
 *
 *   1. TAB ORDER. The hotspots sit on top of a video that spends most of its
 *      life scrubbing. Their buttons, radios and Add buttons must not be
 *      reachable by keyboard, or announced to a screen reader, while the hero
 *      is still moving. CSS can hide a thing; it cannot remove it from the tab
 *      order. That needs the inert attribute, toggled by the lock event.
 *
 *   2. THE CLICK TOGGLE. Hover and keyboard focus reveal a card through CSS
 *      alone. A click is the third way in, and it needs a real state flip so
 *      that aria-expanded on the dot is true when the card is open and false
 *      when it is not. An aria-expanded that never changes is worse than none.
 *
 *   3. ESCAPE, and desktop gating.
 *
 * DESKTOP GATING, AND WHY THE LAYER IS DELETED RATHER THAN HIDDEN
 * The shoppable hero is desktop-only by decision: it is driven by the scroll
 * scrub, which phones do not run, and by hover, which phones do not have.
 * Liquid cannot know the viewport, so the markup ships to everyone and this
 * script removes it on anything that is not a fine-pointer desktop. Removing
 * beats hiding because a hidden subtree still holds tab stops in browsers
 * without inert, and still costs the accessibility tree. The test matches the
 * CSS gate exactly, so the two can never disagree.
 *
 * WHY THE LOCK EVENT AND NOT A SCROLL LISTENER
 * There is one source of truth for whether the hero has settled on its final
 * frame, it lives in assets/sg-scroll-hero.js, and it has hysteresis so a
 * scroll that stops on the boundary cannot flicker this layer on and off. A
 * second scroll listener here would be a second opinion, and the two would
 * disagree at exactly the moment it matters.
 */
(function () {
  'use strict';

  var layer = document.querySelector('[data-sg-hero-shop]');
  if (!layer) return;

  var section = layer.closest('[data-sg-scroll-hero]');
  if (!section) { layer.parentNode.removeChild(layer); return; }

  /* Must stay identical to the media query guarding .sg-hero-shop in
     sections/sg-scroll-hero.liquid. hover/pointer rather than width alone: a
     990px-wide touch tablet has the room but not the input device. */
  var DESKTOP = '(min-width: 990px) and (hover: hover) and (pointer: fine)';

  var ok = false;
  try { ok = window.matchMedia(DESKTOP).matches; } catch (e) { ok = false; }
  if (!ok) { layer.parentNode.removeChild(layer); return; }

  var points = Array.prototype.slice.call(
    layer.querySelectorAll('.sg-hero-shop__pt')
  );
  if (!points.length) { layer.parentNode.removeChild(layer); return; }

  /* ------------------------------------------------------------ open state */

  function close(pt) {
    pt.classList.remove('is-open');
    var dot = pt.querySelector('.sg-hero-shop__dot');
    if (dot) dot.setAttribute('aria-expanded', 'false');
  }

  function closeAll(except) {
    for (var i = 0; i < points.length; i++) {
      if (points[i] !== except) close(points[i]);
    }
  }

  layer.addEventListener('click', function (e) {
    var dot = e.target.closest ? e.target.closest('.sg-hero-shop__dot') : null;
    if (!dot || !layer.contains(dot)) return;

    var pt = dot.closest('.sg-hero-shop__pt');
    if (!pt) return;

    var open = pt.classList.toggle('is-open');
    dot.setAttribute('aria-expanded', open ? 'true' : 'false');

    // Two cards open at once would overlap and there is no reading order that
    // makes that sensible.
    if (open) closeAll(pt);
  });

  /* Escape closes the card and returns focus to its dot, which is where the
     keyboard user came in. Without the focus move, dismissing the card would
     drop focus to the document and the next Tab would restart from the top of
     the page. */
  layer.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;

    var pt = e.target.closest ? e.target.closest('.sg-hero-shop__pt') : null;
    if (!pt) return;

    close(pt);
    var dot = pt.querySelector('.sg-hero-shop__dot');
    if (dot) dot.focus();
    e.stopPropagation();
  });

  /* ------------------------------------------------------------ lock gate */

  function setActive(on) {
    if (on) {
      layer.removeAttribute('inert');
      layer.removeAttribute('aria-hidden');
    } else {
      layer.setAttribute('inert', '');
      layer.setAttribute('aria-hidden', 'true');
      closeAll(null);
    }
    layer.classList.toggle('is-live', !!on);
  }

  section.addEventListener('sg:hero-lock', function (e) {
    setActive(!!(e.detail && e.detail.locked));
  });

  // The markup ships inert, so a browser that never fires the lock event —
  // or a visitor who never scrolls that far — is left with a hero that
  // behaves exactly as it did before this feature existed.
  setActive(section.classList.contains('sg-scrollhero--locked'));
})();
